import { useState } from "react";
import { Play } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  DIRECTIONS,
  DURATION_MAX,
  DURATION_MIN,
  EASINGS,
  isAnimated,
  motionStyle,
  TRANSITION_TYPES,
  usesDirection,
} from "@/lib/slide-transition";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { setTransition } from "@/store/documentSlice";
import type { TransitionDirection, TransitionType } from "@/types/document";

// §10.3 The Transitions right-panel: pick the deck's presentation slide animation and its
// parameters. Writes flow through `setTransition` → the document persistence path (§10.3.4),
// so the choice rehydrates like any deck setting. A live Preview (§10.3.3) replays the
// selected transition on a demo card without entering presentation.
export function TransitionPanel() {
  const dispatch = useAppDispatch();
  const transition = useAppSelector((s) => s.document.transition);
  // Bumping this key remounts the preview card so its enter animation replays (§10.3.3).
  const [previewKey, setPreviewKey] = useState(0);

  const directional = usesDirection(transition.type);

  return (
    <div className="flex h-full flex-col gap-5 overflow-y-auto p-4">
      {/* §10.3.3 Live preview — a 16:9 demo card that plays the enter animation on demand. */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <Label className="text-xs uppercase tracking-wide text-slate-500">
            Preview
          </Label>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPreviewKey((k) => k + 1)}
          >
            <Play className="mr-2 h-4 w-4" /> Play
          </Button>
        </div>
        <div
          className="slide-stage w-full rounded-md border border-slate-200 bg-slate-100"
          style={{ aspectRatio: "16 / 9" }}
        >
          <div
            key={previewKey}
            className={
              isAnimated(transition.type)
                ? "slide-motion slide-entering"
                : "slide-motion"
            }
            style={motionStyle(transition, 1, "enter")}
          >
            <div className="flex h-full w-full flex-col items-center justify-center gap-1 rounded-md bg-gradient-to-br from-indigo-500 to-sky-500 text-white">
              <span className="text-sm font-semibold">Slide</span>
              <span className="text-[10px] uppercase tracking-widest opacity-80">
                {TRANSITION_TYPES.find((t) => t.value === transition.type)
                  ?.label ?? transition.type}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* §10.3.2 Type */}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="transition-type">Transition</Label>
        <Select
          value={transition.type}
          onValueChange={(v) =>
            dispatch(setTransition({ type: v as TransitionType }))
          }
        >
          <SelectTrigger id="transition-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TRANSITION_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value}>
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* The remaining controls do nothing for "none" — hide them to keep the panel honest. */}
      {isAnimated(transition.type) && (
        <>
          {/* §10.3.2 Duration */}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="transition-duration">Duration</Label>
              <span className="tabular-nums text-sm text-slate-500">
                {transition.durationMs} ms
              </span>
            </div>
            <Slider
              id="transition-duration"
              min={DURATION_MIN}
              max={DURATION_MAX}
              step={50}
              value={[transition.durationMs]}
              onValueChange={([v]) =>
                dispatch(setTransition({ durationMs: v }))
              }
            />
          </div>

          {/* §10.3.2 Easing */}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="transition-easing">Easing</Label>
            <Select
              value={transition.easing}
              onValueChange={(v) => dispatch(setTransition({ easing: v }))}
            >
              <SelectTrigger id="transition-easing">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EASINGS.map((e) => (
                  <SelectItem key={e.value} value={e.value}>
                    {e.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* §10.3.2 Direction — only meaningful for slide/push. */}
          {directional && (
            <div className="flex flex-col gap-1.5">
              <Label>Direction</Label>
              <ToggleGroup
                type="single"
                variant="outline"
                value={transition.direction ?? "left"}
                onValueChange={(v) =>
                  v &&
                  dispatch(
                    setTransition({ direction: v as TransitionDirection }),
                  )
                }
                className="justify-start"
              >
                {DIRECTIONS.map((d) => (
                  <ToggleGroupItem key={d.value} value={d.value}>
                    {d.label}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            </div>
          )}
        </>
      )}
    </div>
  );
}
